import {formatDate, formatNumber, formatPercent} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import * as echarts from 'echarts';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {ADB_CHART_LIGHT, adbAlpha, adbCategoryAxis, adbChartTheme, adbRedrawOnThemeChange, adbTooltip, adbValueAxis} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {AdminDashboardReportBlock, AdminDashboardReportProvider, AdminDashboardReportStat} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {adbDrillRowView} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {AdminDashboardDrillView} from '@features/admin-panel/models/admin-dashboard-drill.models';
import {AdminDashboardTrialFunnelDrill} from '@features/admin-panel/models/admin-dashboard-analysis.models';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardTrialFunnel,
  AdminDashboardTrialFunnelMonth,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';

/** The ECharts entry point behind a plain object, so a spec can stub it (the echarts namespace is read-only). */
export const trialFunnelCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/** One stage of the funnel: its count and its share of the started trials (the funnel chart's tooltip). */
export interface AdminDashboardFunnelStage {
  key: 'started' | 'active' | 'converted';
  label: string;
  value: number;
  /** Share of started, 0-100, one decimal. */
  percent: number;
}

/**
 * Trial funnel: the in-scope companies' XatBot trials started in the window (the period, widened to at least 90
 * days), how many were actively tried and how many converted, with the conversion share and the median days to
 * convert. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-trial-funnel',
  templateUrl: './admin-dashboard-trial-funnel.component.html',
  styleUrls: ['./admin-dashboard-trial-funnel.component.scss']
})
export class AdminDashboardTrialFunnelComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => {
    this.renderStages();
    this.render();
  });
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** A company picked with Evidence in the stage drill (ITP-E-161-06): the page opens its why panel. */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  funnel: AdminDashboardTrialFunnel | null = null;
  stages: AdminDashboardFunnelStage[] = [];
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load the trial funnel';

  private chartElement: HTMLDivElement;
  private chart: echarts.ECharts;
  private resizeObserver: ResizeObserver;
  private loadSubscription: Subscription;
  private stageElement: HTMLDivElement;
  private stageChart: echarts.ECharts;
  private stageResizeObserver: ResizeObserver;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry,
    private host: ElementRef<HTMLElement>, private drillService: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('deeper-analysis', this.host.nativeElement, this);
  }

  /**
   * The chart container exists only while there is something to draw: the chart is created when it appears and
   * disposed when it goes.
   */
  @ViewChild('trendChart') set trendChartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.chartElement) {
      return;
    }
    this.disposeChart();
    if (container) {
      this.chartElement = container.nativeElement;
      this.chart = trialFunnelCharts.init(this.chartElement);
      this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
      this.resizeObserver.observe(this.chartElement);
      this.render();
    }
  }

  /** The funnel chart (ITP-E-153-02), created and disposed with its container like the trend chart. */
  @ViewChild('stageChart') set stageChartContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container?.nativeElement === this.stageElement) {
      return;
    }
    this.disposeStageChart();
    if (container) {
      this.stageElement = container.nativeElement;
      this.stageChart = trialFunnelCharts.init(this.stageElement);
      this.stageChart.on('click', (event: any) => this.openStage(this.stages[event?.dataIndex]));
      this.stageResizeObserver = new ResizeObserver(() => this.stageChart?.resize());
      this.stageResizeObserver.observe(this.stageElement);
      this.renderStages();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.disposeChart();
    this.disposeStageChart();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-11). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: 'Trial funnel', text: this.failedTitle}];
    }
    const funnel = this.funnel;
    if (!funnel || funnel.started <= 0) {
      const empty: AdminDashboardReportBlock = {kind: 'empty', title: 'Trial funnel', text: 'No trials started in this window'};
      if (this.subtitle) {
        empty.subtitle = this.subtitle;
      }
      return [empty];
    }
    return [
      {
        kind: 'stats',
        title: 'Trial funnel',
        subtitle: this.subtitle,
        stats: [
          ...this.stages.map((stage): AdminDashboardReportStat => ({label: stage.label, value: formatNumber(stage.value, 'en-US')})),
          {label: 'Started → converted', value: formatPercent(this.conversionRate, 'en-US', '1.0-0')},
          {label: 'Median days to convert', value: String(funnel.medianDaysToConvert ?? '–')}
        ]
      },
      {
        kind: 'chart',
        title: 'Conversion rate by month',
        image: this.chart ? this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}) : null,
        widthPx: this.chart?.getWidth() ?? 0,
        heightPx: this.chart?.getHeight() ?? 0
      }
    ];
  }

  /** Names the window; says so when a short period was widened to 90 days. */
  get subtitle(): string {
    if (!this.funnel) {
      return '';
    }
    if (this.funnel.widened) {
      return 'Trials started in the last 90 days (the period is too short for a funnel).';
    }
    return `Trials started in the last ${this.funnel.windowDays >= 365 ? '12 months' : this.funnel.windowDays + ' days'}.`;
  }

  /** Converted / started, 0-1. */
  get conversionRate(): number {
    return this.funnel?.started ? this.funnel.converted / this.funnel.started : 0;
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardTrialFunnel>(
      'AdminDashboardTrialFunnelQuery', this.params, 'Funnel'
    ).subscribe({
      next: funnel => this.show(funnel ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(funnel: AdminDashboardTrialFunnel | null, failed: boolean): void {
    this.funnel = funnel;
    const started = funnel?.started ?? 0;
    const percent = (value: number): number => started > 0 ? Math.round(value * 1000 / started) / 10 : 0;
    this.stages = funnel ? [
      {key: 'started', label: 'Started', value: started, percent: percent(started)},
      {key: 'active', label: 'Active', value: funnel.active, percent: percent(funnel.active)},
      {key: 'converted', label: 'Converted', value: funnel.converted, percent: percent(funnel.converted)}
    ] : [];
    this.failed = failed;
    this.loaded = true;
    this.renderStages();
    this.render();
    this.report(failed ? 'failed' : started > 0 ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }

  /** "<b>Sep</b><br/>75% converted · 4 trials decided", or "No trials decided". */
  tooltipText(month: AdminDashboardTrialFunnelMonth): string {
    if (!month) {
      return '';
    }
    const body = month.rate === null
      ? 'No trials decided'
      : `${Math.round(month.rate * 100)}% converted · ${countOf(month.decided, 'trial')} decided`;
    return `<b>${month.label}</b><br/>${body}`;
  }

  /** "<b>Active</b><br/>8 trials · 40% of started<br/><muted>Click to list them</muted>": a funnel stage's tooltip. */
  stageTooltipText(stage: AdminDashboardFunnelStage, hintColour: string = ADB_CHART_LIGHT.ink3): string {
    if (!stage) {
      return '';
    }
    return `<b>${stage.label}</b><br/>${countOf(stage.value, 'trial')} · ${formatPercent(stage.percent / 100, 'en-US', '1.0-0')} of started`
      + `<br/><span style="color:${hintColour}">Click to list them</span>`;
  }

  /**
   * A funnel stage was clicked (H6, ITP-E-161-06): the drill lists the trials of that stage with their start day and
   * outcome. A click outside the three stages does nothing. Evidence on a row emits companySelected.
   */
  openStage(stage: AdminDashboardFunnelStage | undefined): void {
    if (!stage || !this.params) {
      return;
    }
    const funnel = this.funnel;
    this.drillService.open({
      title: `Trials — ${stage.key}`,
      columns: [{header: 'Started', align: 'end'}, {header: 'Outcome', align: 'end'}],
      load: () => this.dataService.getWithExtra<AdminDashboardTrialFunnelDrill>(
        'AdminDashboardTrialFunnelDrillQuery', this.params, {Stage: stage.key}
      ).pipe(map(drill => this.stageDrillView(drill, funnel)))
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** "Trials started 17 Jul – 15 Oct." (the drill's window, else the card's), then Started (d MMM) and Outcome per trial. */
  private stageDrillView(drill: AdminDashboardTrialFunnelDrill | null | undefined,
    funnel: AdminDashboardTrialFunnel | null): AdminDashboardDrillView {
    const day = (iso: string): string => formatDate(iso, 'd MMM', 'en-US', 'UTC');
    const from = drill?.From ?? funnel?.from;
    const to = drill?.To ?? funnel?.to;
    const rows = (drill?.Rows ?? []).map(trial => adbDrillRowView(trial, [
      {text: day(trial.started)},
      {text: trial.outcome === 'converted' ? (trial.convertedOn ? `Converted ${day(trial.convertedOn)}` : 'Converted')
          : trial.outcome === 'lapsed' ? 'Lapsed' : 'Running'}
    ]));
    return {
      subtitle: from && to ? `Trials started ${day(from)} – ${day(to)}.` : '',
      rows,
      total: drill?.Total ?? rows.length
    };
  }

  private disposeChart(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.chart?.dispose();
    this.chart = null;
    this.chartElement = null;
  }

  private disposeStageChart(): void {
    this.stageResizeObserver?.disconnect();
    this.stageResizeObserver = null;
    this.stageChart?.dispose();
    this.stageChart = null;
    this.stageElement = null;
  }

  /**
   * The OCA-E-159 funnel (M/charts.ts funnelOption): Started in the light blue, Active in violet (the theme purple at
   * 55%), Converted in the bar green; labels inside. `max` is the started count, so Started fills the width.
   */
  private renderStages(): void {
    if (!this.stageChart) {
      return;
    }
    const t = adbChartTheme(this.host.nativeElement);
    const colours: {[key: string]: string} = {started: t.blue2, active: adbAlpha(t.purple, 0.55), converted: t.greenBar};
    const option: echarts.EChartsOption = {
      tooltip: adbTooltip(t, {
        trigger: 'item',
        formatter: (point: any) => this.stageTooltipText(this.stages[point?.dataIndex], t.ink3)
      }),
      series: [{
        type: 'funnel',
        left: 8,
        right: 8,
        top: 8,
        bottom: 8,
        min: 0,
        max: Math.max(this.funnel?.started ?? 0, 1),
        sort: 'none',
        gap: 3,
        minSize: '30%',
        label: {
          show: true,
          position: 'inside',
          color: t.ink,
          fontSize: 12,
          formatter: (point: any) => `${point.name}  ${formatNumber(point.value, 'en-US', '1.0-0')}`
        },
        itemStyle: {borderWidth: 0, borderRadius: 4},
        data: this.stages.map(stage => ({name: stage.label, value: stage.value, itemStyle: {color: colours[stage.key]}}))
      }]
    };
    this.stageChart.setOption(option, true);
  }

  /** The conversion trend (M/charts.ts rateTrendOption): the theme green line over a faint green area. */
  private render(): void {
    if (!this.chart) {
      return;
    }
    const trend = this.funnel?.trend ?? [];
    const t = adbChartTheme(this.host.nativeElement);
    const option: echarts.EChartsOption = {
      grid: {left: 36, right: 8, top: 10, bottom: 22},
      xAxis: adbCategoryAxis(t, trend.map(m => m.label), {boundaryGap: false, axisLabel: {color: t.ink2, fontSize: 10}}),
      yAxis: adbValueAxis(t, (value: number) => `${Math.round(value * 100)}%`, {min: 0, max: 1, splitNumber: 2}),
      series: [{
        type: 'line',
        data: trend.map(m => m.rate),
        connectNulls: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: {width: 2, color: t.green},
        itemStyle: {color: t.green},
        areaStyle: {color: t.green, opacity: 0.08}
      }],
      tooltip: adbTooltip(t, {
        formatter: (points: any) => {
          const point = Array.isArray(points) ? points[0] : points;
          return this.tooltipText(trend[point?.dataIndex]);
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
